import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { controller } from '../../api/controller';
import { usePublicUiLocale } from '../../contexts/PublicUiLocaleContext';
import HelpHint from '../../components/HelpHint';

function parseApiMessage(err, fallback) {
  const msg = err?.response?.data?.message;
  if (typeof msg === 'string') return msg;
  if (typeof msg === 'object' && msg !== null && typeof msg.message === 'string') {
    return msg.message;
  }
  return fallback;
}

export default function UidRenameAttachDialog({
  open,
  uid,
  projectSlug,
  onClose,
  onAttached,
}) {
  const { sa } = usePublicUiLocale();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && uid) {
      setName(uid.name || '');
      setError('');
    }
  }, [open, uid]);

  const handleSubmit = async () => {
    if (!uid?.id || !projectSlug) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError(sa.uidRenameRequired);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (trimmed !== String(uid.name || '').trim()) {
        await controller.updateUid(uid.id, { name: trimmed });
      }
      await controller.post('/catalog/projects/uids/link-by-code', {
        projectSlug,
        uidCode: uid.code,
      });
      onAttached?.();
      onClose();
    } catch (e) {
      setError(parseApiMessage(e, sa.attachFailed));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {sa.renameUidToAttach}
        <HelpHint title={sa.helpUidAttachTitle} text={sa.helpUidAttach} />
      </DialogTitle>
      <DialogContent>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={sa.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText={sa.renameUidPrompt}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{sa.cancel}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? sa.creating : sa.attachTo}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UidRenameAttachDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  uid: PropTypes.shape({
    id: PropTypes.number.isRequired,
    code: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  projectSlug: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onAttached: PropTypes.func,
};
